import type { AxiosInstance } from 'axios'
import { isErrorStatus } from '@audience-republic/audience-manager-shared/api/utils'
import type { CustomApiError } from '@audience-republic/audience-manager-shared/api/types'
import type { EmailSenderProperty } from './types'

export default (axios: AxiosInstance) => ({
  /**
   * Fetch all email senders (verification properties) for a promoter. Used to
   * populate the "From" picker. Verification state lives on each item's
   * `verified` flag; callers decide whether to filter unverified senders out.
   */
  async fetchAll(promoterOid: number): Promise<EmailSenderProperty[]> {
    const { data, status } = await axios.get(`promoter/${promoterOid}/property`, {
      params: {
        $top: 'all',
        $count: true,
        $filter: 'type = promoter-email-verification',
      },
    })
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Fetch email senders error',
        message: `Error fetching email senders`,
        status,
      }
      throw apiError
    }
    // The endpoint may return either a bare array or a paged { rows } envelope.
    return Array.isArray(data) ? data : (data?.rows ?? [])
  },

  async get(
    promoterOid: number,
    oid: number,
  ): Promise<EmailSenderProperty> {
    const { data, status }: {
      data: EmailSenderProperty; status: number
    } = await axios.get(`promoter/${promoterOid}/property/${oid}`)
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Fetch email sender error',
        message: `Error fetching email sender`,
        status,
      }
      throw apiError
    }

    return data
  },

  async create(body: object): Promise <any> {
    const { promoterOid, payload }: any = body
    const { data, status } = await axios.post(`promoter/${promoterOid}/sender`, {
      payload,
    })

    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Failed to create message sender',
        message: `The server could not create message sender`,
        status: status,
      }
      throw apiError
    }
    return data
  },
})
